import { useState, useRef, useEffect, useCallback } from "react";
import RecorderVisualizer from "./recorder-visualizer";
import { formatTime } from "./utils/format-timer";
import { useAudioStream } from "@/hooks/useAudioStream";

interface CustomAudioPlayerProps {
  mediaBlobUrl: string;
}

const RecorderAudioPlayer: React.FC<CustomAudioPlayerProps> = ({
  mediaBlobUrl,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioStream, setAudioStream] = useAudioStream(audioRef, mediaBlobUrl);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, [mediaBlobUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = (): void => {
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    return (): void => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [mediaBlobUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleGetDuration = (event: Event): void => {
      const target = event.target as HTMLAudioElement;
      target.currentTime = 0;
      target.removeEventListener("timeupdate", handleGetDuration);
      setDuration(target.duration);
    };

    const handleLoadedMetadata = (): void => {
      if (audio.duration === Infinity || isNaN(audio.duration)) {
        audio.currentTime = 1e101;
        audio.addEventListener("timeupdate", handleGetDuration);
      } else {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    return (): void => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleGetDuration);
    };
  }, [mediaBlobUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = (): void => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.load();

      const handleLoaded = (): void => {
        if (audioRef.current) {
          const audioElement = audioRef.current as unknown as {
            captureStream: () => MediaStream;
          };
          const stream = audioElement.captureStream();
          setAudioStream(stream);
        }
        audio.removeEventListener("loadedmetadata", handleLoaded);
      };
      audio.addEventListener("loadedmetadata", handleLoaded);
    };

    audio.addEventListener("ended", handleEnded);
    return (): void => {
      audio.removeEventListener("ended", handleEnded);
    };
  }, [mediaBlobUrl, setAudioStream]);

  const togglePlayPause = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      if (!audioStream) {
        const audioElement = audio as unknown as {
          captureStream: () => MediaStream;
        };
        const stream = audioElement.captureStream();
        setAudioStream(stream);
      }
      void audio.play();
      setIsPlaying(true);
    }
  }, [audioStream, isPlaying, setAudioStream]);

  return (
    <div className="flex flex-col gap-1 w-full">
      <audio
        ref={audioRef}
        src={mediaBlobUrl}
        style={{ visibility: "hidden", width: 0, height: 0 }}
      />
      <RecorderVisualizer
        controls
        audio={audioStream}
        paused={!isPlaying}
        onToggle={togglePlayPause}
      />
      <div className="text-center">
        <p className="font-medium">
          {formatTime(currentTime)} / {formatTime(duration)}
        </p>
      </div>
    </div>
  );
};

export default RecorderAudioPlayer;
